import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { supabase } from "@/lib/supabase/client"
import { isMockMode } from "@/lib/mock/mock-supabase"
import { useAuth } from "@/hooks/useAuth"
import { useTenant } from "@/hooks/useTenant"
import type { AppNotification } from "@/types/index"

const TABLE = "credit_v2_notifications"

export interface UseNotificationsReturn {
  notifications: AppNotification[]
  unreadCount: number
  isLoading: boolean
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  removeNotification: (id: string) => Promise<void>
  /** Remove TODAS as notificações do usuário no tenant atual (DELETE no DB). */
  removeAllNotifications: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { user } = useAuth()
  const { currentTenant } = useTenant()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)

  const userId = user?.id
  const tenantId = currentTenant?.id

  const fetchNotifications = useCallback(async () => {
    if (!userId || !tenantId || isMockMode) {
      setNotifications([])
      return
    }

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select("*")
        .eq("user_id", userId)
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(100)

      if (error) throw error
      setNotifications((data as AppNotification[]) ?? [])
    } catch (err) {
      console.error("Erro ao carregar notificações:", err)
    } finally {
      setIsLoading(false)
    }
  }, [userId, tenantId])

  // Realtime subscription
  useEffect(() => {
    if (!userId || !tenantId || isMockMode) {
      setNotifications([])
      return
    }

    void fetchNotifications()

    const channelName = `notifications:${userId}:${tenantId}`
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: TABLE,
          filter: `user_id=eq.${userId}`,
        },
        (payload: { eventType: string; new: AppNotification; old: { id: string } }) => {
          if (payload.eventType === "INSERT") {
            const incoming = payload.new as AppNotification
            if (incoming.tenant_id === tenantId) {
              setNotifications((prev) => [incoming, ...prev])
            }
          } else if (payload.eventType === "UPDATE") {
            const updated = payload.new as AppNotification
            setNotifications((prev) =>
              prev.map((n) => (n.id === updated.id ? updated : n)),
            )
          } else if (payload.eventType === "DELETE") {
            const deletedId = payload.old?.id
            if (deletedId) {
              setNotifications((prev) => prev.filter((n) => n.id !== deletedId))
            }
          }
        },
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      void supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [userId, tenantId, fetchNotifications])

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at).length,
    [notifications],
  )

  const markAsRead = useCallback(
    async (id: string) => {
      if (isMockMode) return
      // Optimistic
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        ),
      )
      const { error } = await supabase
        .from(TABLE)
        .update({ read_at: new Date().toISOString() })
        .eq("id", id)
      if (error) {
        console.error("Erro ao marcar como lida:", error)
        void fetchNotifications()
      }
    },
    [fetchNotifications],
  )

  const markAllAsRead = useCallback(async () => {
    if (isMockMode || !userId || !tenantId) return
    const now = new Date().toISOString()
    // Optimistic
    setNotifications((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: now })),
    )
    const { error } = await supabase
      .from(TABLE)
      .update({ read_at: now })
      .eq("user_id", userId)
      .eq("tenant_id", tenantId)
      .is("read_at", null)
    if (error) {
      console.error("Erro ao marcar todas como lidas:", error)
      void fetchNotifications()
    }
  }, [userId, tenantId, fetchNotifications])

  const removeNotification = useCallback(
    async (id: string) => {
      if (isMockMode) return
      // Optimistic
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      const { error } = await supabase.from(TABLE).delete().eq("id", id)
      if (error) {
        console.error("Erro ao remover notificação:", error)
        void fetchNotifications()
      }
    },
    [fetchNotifications],
  )

  // task #116 follow-up: remove TODAS as notificações do user no tenant atual.
  // Optimistic update — limpa UI imediatamente, faz DELETE em batch no DB
  // (RLS já restringe à `user_id = auth.uid()`, então é seguro).
  // Em erro, refetch traz o estado real.
  const removeAllNotifications = useCallback(
    async () => {
      if (isMockMode || !userId || !tenantId) return
      const ids = notifications.map((n) => n.id)
      if (ids.length === 0) return
      setNotifications([])
      const { error } = await supabase
        .from(TABLE)
        .delete()
        .eq("user_id", userId)
        .eq("tenant_id", tenantId)
        .in("id", ids)
      if (error) {
        console.error("Erro ao limpar todas as notificações:", error)
        void fetchNotifications()
      }
    },
    [userId, tenantId, notifications, fetchNotifications],
  )

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    removeNotification,
    removeAllNotifications,
  }
}
